import React, { useEffect, useRef, useState } from 'react';
import { loadPayPalSdk } from '../services/commerce/commerceService.js';

export default function DistributionCheckout({ session }) {
  const [option, setOption] = useState('free_watchvim_prime');
  const [subscriptionId, setSubscriptionId] = useState('');
  const [message, setMessage] = useState('');
  const host = useRef(null);
  const wrapper = useRef(null);
  const activeIdentity = useRef('');
  const checkoutKey = useRef('');
  const email = session?.user?.email || '';
  const token = session?.access_token || '';

  function identity(form) {
    const data = new FormData(form);
    return JSON.stringify([String(email || data.get('email') || '').trim().toLowerCase(),
      String(data.get('title') || '').trim().replace(/\s+/g, ' ').toLowerCase()]);
  }
  useEffect(() => {
    const form = wrapper.current?.closest('form');
    if (!form) return undefined;
    function restore() {
      const key = identity(form);
      if (key === activeIdentity.current) return;
      activeIdentity.current = key;
      checkoutKey.current = crypto.randomUUID();
      let saved = '';
      try { saved = sessionStorage.getItem('wv-distribution:' + key) || ''; } catch (_) {}
      setSubscriptionId(saved);
      setMessage(saved ? 'PayPal approval saved for this title. Submit below to verify payment and send your title for review.' : '');
    }
    function reset() {
      try { sessionStorage.removeItem('wv-distribution:' + activeIdentity.current); } catch (_) {}
      setOption('free_watchvim_prime'); setSubscriptionId(''); setMessage('');
      activeIdentity.current = ''; checkoutKey.current = '';
    }
    restore();
    form.addEventListener('input', restore);
    form.addEventListener('reset', reset);
    return () => { form.removeEventListener('input', restore); form.removeEventListener('reset', reset); };
  }, [email]);

  useEffect(() => {
    if (option !== 'extended_annual' || subscriptionId) return undefined;
    let cancelled = false;
    let buttons;
    let checkoutIdentity = '';
    const form = wrapper.current?.closest('form');
    setMessage('Loading PayPal checkout…');
    loadPayPalSdk({ intent: 'subscription' }).then(async sdk => {
      if (cancelled || !host.current) return;
      buttons = sdk.Buttons({
        style: { layout: 'vertical', color: 'gold', shape: 'rect', label: 'subscribe', height: 48 },
        createSubscription: async () => {
          if (!form.reportValidity()) {
            const error = new Error('Complete all required title details and acknowledgements above before starting PayPal checkout.');
            setMessage(error.message);
            throw error;
          }
          const data = new FormData(form);
          const boundIdentity = identity(form);
          let saved;
          try { saved = sessionStorage.getItem('wv-distribution:' + boundIdentity); } catch (_) {}
          if (saved) { setSubscriptionId(saved); throw new Error('Approval already saved. Submit your title below; do not pay again.'); }
          checkoutIdentity = boundIdentity;
          activeIdentity.current = boundIdentity;
          checkoutKey.current ||= crypto.randomUUID();
          const response = await fetch('/api/submissions/payment', {
            method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: 'Bearer ' + token } : {}) },
            body: JSON.stringify({ email: email || data.get('email'), title: data.get('title'), portal: !!token, checkoutKey: checkoutKey.current })
          });
          const result = await response.json();
          if (!response.ok) throw new Error(result.error || 'Unable to start checkout.');
          return result.subscriptionId;
        },
        onApprove: data => {
          if (cancelled) return;
          if (!data.subscriptionID) { setMessage('PayPal did not return an approval reference. Please contact streaming@watchvim.com.'); return; }
          try { sessionStorage.setItem('wv-distribution:' + checkoutIdentity, data.subscriptionID); } catch (_) {}
          if (identity(form) !== checkoutIdentity) {
            setMessage('Approval saved for your original title and email. Restore those details to submit without paying again.');
            return;
          }
          setSubscriptionId(data.subscriptionID);
          setMessage('PayPal approval saved. Submit below to verify payment and send your title for review.');
        },
        onCancel: () => setMessage('Checkout canceled. No title has been submitted.'),
        onError: error => setMessage(error?.message || 'PayPal checkout failed. Please try again.')
      });
      await buttons.render(host.current);
      if (!cancelled) setMessage('Complete your title details, approve PayPal, then submit below.');
    }).catch(error => { if (!cancelled) setMessage(error.message || 'Checkout unavailable.'); });
    return () => { cancelled = true; try { buttons?.close(); } catch (_) {} };
  }, [option, subscriptionId, email, token]);

  return <div ref={wrapper}>
    <label>Distribution option<select name="distribution_option" value={option} onChange={event => setOption(event.target.value)}>
      <option value="free_watchvim_prime">Free — WatchVIM + eligible Prime review</option>
      <option value="extended_annual">Extended distribution — $39.99/year + $5 setup</option>
      <option value="short_run_watchvim_only">WatchVIM-only short run</option>
    </select></label>
    <input type="hidden" name="content_distribution_paypal_subscription_id" value={option === 'extended_annual' ? subscriptionId : ''} />
    {option === 'extended_annual' ? <section className="business-card" aria-label="Extended distribution checkout">
      <h3>Extended distribution</h3>
      <p>$44.99 initially: $39.99 for the first year plus a one-time $5 setup fee. Renews at $39.99 annually until canceled through PayPal.</p>
      <p>Distribution remains subject to title review and platform acceptance. Use the same title and submission email after approving payment.</p>
      {!subscriptionId ? <div ref={host} className="paypal-button-host" /> : null}
      <p role="status">{message}</p>
    </section> : null}
  </div>;
}
